import React from 'react';

/**
 * ErrorBoundary catches a render-time exception and shows what happened.
 *
 * Without one, React unmounts the whole tree on any thrown render — the screen
 * goes black with nothing on it, and the only way to learn why is to open the
 * developer console, which an operator has no reason to do. A blank page also
 * looks identical to the server being down, so it sends people to check the
 * wrong thing.
 *
 * It deliberately does not try to recover by itself: the component that threw
 * would be re-rendered from the same state and throw again. Reloading is the
 * honest offer, and the message says what to hand over if it keeps happening.
 */
type Props = { children: React.ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the component stack: the message below shows the error, but which
    // screen was rendering is what actually locates it.
    console.error('render failed', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ padding: 32, maxWidth: 720, margin: '0 auto', fontFamily: 'inherit' }}>
        <h1 style={{ marginBottom: 8 }}>This screen failed to render</h1>
        <p className="muted" style={{ marginBottom: 16 }}>
          Nothing was saved or changed by this — it happened while drawing the page. Reloading
          usually clears it. If it comes back on the same screen, this is the part worth reporting:
        </p>
        <pre style={{
          background: 'var(--bg-1)', padding: 12, borderRadius: 6, overflowX: 'auto',
          fontSize: 12.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{error.message || String(error)}</pre>
        <button className="btn btn-primary" style={{ marginTop: 16 }}
                onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}
